// src/components/admin/UserManagement.js
// Users are created via Firebase Authentication in the shared auth system.
// This component only manages existing users: view, search, edit role/group, delete.
import '../../styles/userManagement.css';
import { db } from '../../firebase';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  getDocs,
  getDoc,
  query,
  updateDoc,
  doc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  where
} from 'firebase/firestore';
import AssignmentEvaluationModal from './AssignmentEvaluationModal';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Edit form state
  const [editingUser, setEditingUser] = useState(null);
  const [name, setName] = useState('');
  const [role, setRole] = useState('learner');
  const [selectedGroupId, setSelectedGroupId] = useState('');

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Program-enrollment filter — learners only show up here if they're
  // enrolled in a catalog with type 'program'. null = not loaded yet
  // (don't filter, avoids hiding everyone while data is in flight or on error).
  const [programLearnerIds, setProgramLearnerIds] = useState(null);
  const [showAllLearners, setShowAllLearners] = useState(false);

  // Which learner is currently open in the assignment/evaluation modal
  const [evalModalUser, setEvalModalUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(query(collection(db, 'users')));
      setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const snapshot = await getDocs(query(collection(db, 'groups')));
      setGroups(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError('Failed to load groups: ' + err.message);
    }
  }, []);

  // Which learners are enrolled in at least one 'program' catalog (as
  // opposed to a language-level catalog item)? Two reads: program catalog
  // ids, then userCatalogs rows pointing at any of them. Chunked at 30 per
  // 'in' query — you're well under that today, but this won't silently
  // break if the catalog count grows.
  const fetchProgramLearnerIds = useCallback(async () => {
    try {
      const catalogsSnap = await getDocs(
        query(collection(db, 'catalogs'), where('type', '==', 'program'))
      );
      const programCatalogIds = catalogsSnap.docs.map(d => d.id);

      if (programCatalogIds.length === 0) {
        setProgramLearnerIds(new Set());
        return;
      }

      const chunkSize = 30;
      const chunks = [];
      for (let i = 0; i < programCatalogIds.length; i += chunkSize) {
        chunks.push(programCatalogIds.slice(i, i + chunkSize));
      }

      const idSets = await Promise.all(
        chunks.map(async (ids) => {
          const snap = await getDocs(
            query(collection(db, 'userCatalogs'), where('catalogId', 'in', ids))
          );
          return snap.docs.map(d => d.data().userId);
        })
      );

      setProgramLearnerIds(new Set(idSets.flat()));
    } catch (err) {
      console.error('Error fetching program enrollments:', err);
      // Fail open — show every learner rather than hiding the whole list
      // because one query broke.
      setProgramLearnerIds(null);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchGroups();
    fetchProgramLearnerIds();
  }, [fetchUsers, fetchGroups, fetchProgramLearnerIds]);

  useEffect(() => {
    let timer;
    if (success || error) {
      timer = setTimeout(() => { setSuccess(null); setError(null); }, 5000);
    }
    return () => clearTimeout(timer);
  }, [success, error]);

  const resetForm = () => {
    setEditingUser(null);
    setName('');
    setRole('learner');
    setSelectedGroupId('');
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser?.id) { setError("Invalid user data."); return; }

    try {
      setLoading(true);
      setError(null);
      const userDocRef = doc(db, 'users', editingUser.id);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) throw new Error('User not found.');

      const previousGroupId = editingUser.groupId || null;
      const newGroupId = selectedGroupId || null;
      const arrayField = role === 'mentor' ? 'mentorIds' : 'learnerIds';

      // Update the user document
      await updateDoc(userDocRef, {
        name,
        role,
        groupId: newGroupId,
        updatedAt: serverTimestamp()
      });

      // Remove user from old group's array
      if (previousGroupId && previousGroupId !== newGroupId) {
        const oldGroupRef = doc(db, 'groups', previousGroupId);
        const oldGroupDoc = await getDoc(oldGroupRef);
        if (oldGroupDoc.exists()) {
          // Remove from both arrays in case role changed
          await updateDoc(oldGroupRef, {
            learnerIds: arrayRemove(editingUser.id),
            mentorIds: arrayRemove(editingUser.id)
          });
        }
      }

      // Add user to new group's array
      if (newGroupId) {
        const newGroupRef = doc(db, 'groups', newGroupId);
        const newGroupDoc = await getDoc(newGroupRef);
        if (newGroupDoc.exists()) {
          // If role changed, also remove from the other array
          if (previousGroupId === newGroupId) {
            const otherField = arrayField === 'mentorIds' ? 'learnerIds' : 'mentorIds';
            await updateDoc(newGroupRef, {
              [arrayField]: arrayUnion(editingUser.id),
              [otherField]: arrayRemove(editingUser.id)
            });
          } else {
            await updateDoc(newGroupRef, {
              [arrayField]: arrayUnion(editingUser.id)
            });
          }
        }
      }

      setSuccess('User updated successfully');
      resetForm();
      fetchUsers();
      fetchGroups();
    } catch (err) {
      setError('Failed to update user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = useCallback(async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This only removes them from Firestore — also delete them from Firebase Authentication.')) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'users', userId));
      setSuccess('User deleted from Firestore. Remember to also remove them from Firebase Authentication.');
      fetchUsers();
    } catch (err) {
      setError('Failed to delete user: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  const startEditing = useCallback((user) => {
    if (!user?.id) { setError("Unable to edit this user: missing data"); return; }
    setEditingUser(user);
    setName(user.name || '');
    setRole(user.role || 'learner');
    setSelectedGroupId(user.groupId || '');
  }, []);

  const displayedUsers = useMemo(() => {
    // This app only has 3 roles conceptually, but `users` is a shared
    // collection across apps in the same Firebase project — a doc could
    // carry role 'tutor', 'official', etc. from the LEARN app. Admins are
    // also excluded here on purpose: this view is for managing learners
    // and mentors, not for browsing admin accounts.
    const relevantUsers = users.filter(u => u.role === 'learner' || u.role === 'mentor');

    const withGroups = relevantUsers.map(user => ({
      ...user,
      userGroup: groups.find(g => g.id === user.groupId) || null
    }));

    const programFiltered = (programLearnerIds === null || showAllLearners)
      ? withGroups
      : withGroups.filter(u => u.role !== 'learner' || programLearnerIds.has(u.id));

    const filtered = searchTerm
      ? programFiltered.filter(u =>
          u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.userGroup?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : programFiltered;

    return [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [users, groups, searchTerm, programLearnerIds, showAllLearners]);

  const hiddenLearnerCount = useMemo(() => {
    if (programLearnerIds === null || showAllLearners) return 0;
    return users.filter(u => u.role === 'learner' && !programLearnerIds.has(u.id)).length;
  }, [users, programLearnerIds, showAllLearners]);

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>User Management</h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Edit form — shown inline when a user is selected */}
      {editingUser && (
        <div className="user-form-container">
          <h3>Edit: {editingUser.email}</h3>
          <form onSubmit={handleUpdateUser}>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select id="role" value={role} onChange={(e) => setRole(e.target.value)} required>
                <option value="learner">Learner</option>
                <option value="mentor">Mentor</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="group">Group</label>
              <select id="group" value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)}>
                <option value="">— No Group —</option>
                {groups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name || "Unnamed Group"}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Update User'}
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="search-container" style={{ margin: '16px 0' }}>
        <input
          type="text"
          placeholder="Search by name, email, role or group..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
          >
            ✕
          </button>
        )}
        <label style={{ marginLeft: '16px', fontSize: '0.85rem', color: '#666', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showAllLearners}
            onChange={(e) => setShowAllLearners(e.target.checked)}
            style={{ marginRight: '6px' }}
          />
          Show all learners (including those not enrolled in a program)
        </label>
      </div>

      {hiddenLearnerCount > 0 && (
        <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: '0 0 12px' }}>
          {hiddenLearnerCount} learner{hiddenLearnerCount !== 1 ? 's' : ''} hidden — not enrolled in a program catalog.
        </p>
      )}

      {loading && !editingUser ? (
        <div>Loading users...</div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Assignments</th>
                <th>Role</th>
                <th>Group</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.length === 0 ? (
                <tr>
                  <td colSpan="5">
                    {searchTerm ? 'No users match your search.' : 'No users found.'}
                  </td>
                </tr>
              ) : (
                displayedUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>
                      {user.role === 'learner' ? (
                        <button
                          onClick={() => setEvalModalUser(user)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#2563eb', padding: 0, font: 'inherit',
                          }}
                          title="View assignments and evaluate">
                          View / Evaluate
                        </button>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td>{user.role}</td>
                    <td>{user.userGroup ? user.userGroup.name : '—'}</td>
                    <td>
                      <button className="edit-button" onClick={() => startEditing(user)}>
                        Edit
                      </button>
                      <button className="delete-button" onClick={() => handleDeleteUser(user.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {evalModalUser && (
        <AssignmentEvaluationModal
          learner={evalModalUser}
          onClose={() => setEvalModalUser(null)}
        />
      )}
    </div>
  );
}

export default UserManagement;